// ProjectListScreen.js
"use strict";
import {
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GET_PROJECTS_PAGINATED } from "../ApiEndpoints";
import {
  convertToDate,
  getLanguageName,
  getTaskName,
} from "../utils/generalFunctions.js";
import { ToastContainer } from "react-toastify";
import classes from "../Assets/Styles/Global.module.css";
import GlobalButton from "../Components/Buttons/GlobalButton.js";
import { primaryColor, secondaryColor } from "../utils/constants.js";
import { searchBarSx, nameCellSx, selectMinWidthSx } from "../theme.js";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { Store } from "../Store.js";

const PAGE_SIZE = 25;

const ProjectListScreen = () => {
  const navigate = useNavigate();
  const { dispatch: ctxDispatch } = useContext(Store);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);         // for pagination page fetches
  const [initializing, setInitializing] = useState(true); // first page load
  const [searchTerm, setSearchTerm] = useState("");
  const [lastDocId, setLastDocId] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // guards / refs
  const ran = useRef(false);
  const didRunSearchOnce = useRef(false);
  const loadMoreRef = useRef(null);
  const ioRef = useRef(null);

  // (Remote behavior) clear project file on landing
  useEffect(() => {
    ctxDispatch({ type: "PROJECT_FILE_CLEAR" });
  }, [ctxDispatch]);

  const fetchPage = useCallback(
    async ({ reset = false, cursor = null, search = "" } = {}) => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.set("pageSize", PAGE_SIZE.toString());
        if (!reset && cursor) params.set("lastDocId", cursor);
        if (search.trim()) params.set("search", search.trim());

        const { data } = await axios.get(
          `${GET_PROJECTS_PAGINATED}?${params.toString()}`
        );

        const incoming = data.projectsArray || [];
        setProjects((prev) => (reset ? incoming : [...prev, ...incoming]));
        setLastDocId(data.lastDocId || null);
        setHasMore(!!data.hasMore);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Initial load
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let cancelled = false;
    (async () => {
      setInitializing(true);
      await fetchPage({ reset: true, cursor: null, search: "" });
      if (!cancelled) setInitializing(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchPage]); // mount only

  // Debounced server-side search (keep functionality; no client-side filter)
  useEffect(() => {
    if (!didRunSearchOnce.current) {
      didRunSearchOnce.current = true; // skip first run on mount
      return;
    }

    const t = setTimeout(() => {
      setLastDocId(null);
      setHasMore(true);
      setProjects([]);
      fetchPage({ reset: true, cursor: null, search: searchTerm }); // uses current searchTerm
    }, 800);

    return () => clearTimeout(t);
  }, [searchTerm, fetchPage]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchPage({ reset: false, cursor: lastDocId, search: searchTerm });
  }, [fetchPage, hasMore, lastDocId, loading, searchTerm]);

  // Infinite scroll sentinel
  useEffect(() => {
    if (ioRef.current) ioRef.current.disconnect();

    ioRef.current = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !loading && hasMore && !initializing) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: "600px 0px 600px 0px",
        threshold: 0,
      }
    );

    const node = loadMoreRef.current;
    if (node) ioRef.current.observe(node);

    return () => {
      if (ioRef.current) ioRef.current.disconnect();
    };
  }, [hasMore, loading, initializing, loadMore]);

  // Get most recent dubbed video URL for a language
  const getLatestVideoUrl = (videos = [], language) => {
    const filtered = videos.filter((v) => v.language === language);
    const sorted = filtered.sort((a, b) => {
      const aMs =
        (a.creationDate?.seconds || 0) * 1000 +
        Math.floor((a.creationDate?.nanoseconds || 0) / 1e6);
      const bMs =
        (b.creationDate?.seconds || 0) * 1000 +
        Math.floor((b.creationDate?.nanoseconds || 0) / 1e6);
      return bMs - aMs;
    });
    return sorted[0]?.url || null;
  };

  return (
    <Grid item container justifyContent="center">
      <ToastContainer position="top-center" />

      <Grid item container xs={10} marginTop={7} direction="row" marginBottom={10}>
        <Grid
          item
          container
          xs={12}
          marginBottom={1.5}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid
            item
            sx={{ fontSize: "var(--font-size-header)", fontWeight: "900" }}
            className={classes["header"]}
          >
            View Projects
          </Grid>
          <Grid item>
            <TextField
              type="text"
              size="small"
              placeholder="Search..."
              sx={{ ...searchBarSx, width: 250, mb: 0 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
        </Grid>

        {initializing ? (
          <Grid item xs={12} sx={{ width: 1200 }}>
            {Array.from({ length: 10 }).map((_, index) => (
              <Skeleton key={index} animation="wave" width="100%" height={60} />
            ))}
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead sx={{ backgroundColor: `${secondaryColor}` }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Work Pages</TableCell>
                  <TableCell>Watch</TableCell>
                  <TableCell>Creation Date</TableCell>
                  <TableCell>Admin Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {projects.map((project, index) => (
                  <TableRow
                    key={`${project.id}-${index}`}
                    sx={{ "& .MuiTableCell-root": { py: 1 } }} // tightened rows
                  >
                    {/* Simplified/tightened Name cell */}
                    <TableCell sx={nameCellSx}>
                      <Grid container alignItems="center" wrap="nowrap">
                        <Grid
                          item
                          sx={{
                            fontFamily: "var(--font-heading)",
                            fontWeight: "var(--weight-heading)",
                            color: "var(--color-link-text)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 350,
                          }}
                          title={project.name}
                        >
                          {project.name}
                        </Grid>
                        <Grid item sx={{ ml: 1, flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            sx={{
                              color: `${primaryColor}`,
                              "&:hover": {
                                backgroundColor: `${primaryColor}`,
                                color: `${secondaryColor}`,
                              },
                            }}
                            onClick={() =>
                              navigate(`/projects/${project.id}/manage`)
                            }
                          >
                            <EditNoteIcon sx={{ fontSize: "1.2rem" }} />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </TableCell>

                    <TableCell>
                      <GlobalButton
                        onClick={() => navigate(`/workpages/${project.id}`)}
                        height="45px"
                        width="120px"
                        className={classes.listButton}
                        // If GlobalButton forwards sx/style -> keep both for safety.
                        sx={{ whiteSpace: 'nowrap' }}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Work Pages
                      </GlobalButton>
                    </TableCell>

                    {/* “Watch” column (remote design), server data-driven */}
                    <TableCell width="10px">
                      <Grid item container justifyContent="center">
                        <Select
                          size="small"
                          color="primary"
                          sx={selectMinWidthSx}
                          value=""
                          displayEmpty
                          renderValue={() => "Watch 🎞️"}
                          onChange={(e) => {
                            if (e.target.value) {
                              window.open(e.target.value, "_blank", "noopener,noreferrer");
                            }
                          }}
                        >
                          <MenuItem value="" disabled>
                            🎞️ Watch
                          </MenuItem>
                          {[project.orginalLang, ...(project.finalLang || [])].map(
                            (language, idx) => {
                              const isOriginal = idx === 0;
                              const videoUrl = isOriginal
                                ? project.videoUrl
                                : getLatestVideoUrl(project.dubbedVideosList || [], language);

                              return (
                                <MenuItem
                                  key={`${project.id}-${language}`}
                                  value={videoUrl || ""}
                                  disabled={!videoUrl}
                                >
                                  🎞️ {getLanguageName(language)}
                                </MenuItem>
                              );
                            }
                          )}
                        </Select>
                      </Grid>
                    </TableCell>

                    <TableCell>
                      <Grid container width="100%" justifyContent="center">
                        <Grid item>{convertToDate(project.creationDate)}</Grid>
                      </Grid>
                    </TableCell>

                    <TableCell>
                      <Grid
                        item
                        container
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                      >
                        <Grid item>
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/actions/${project.id}`)}
                          >
                            <span role="img" aria-label="actions">⚙️</span>
                          </IconButton>
                        </Grid>
                        <Grid item>
                          <Select
                            size="small"
                            color="primary"
                            defaultValue=""
                            displayEmpty
                            sx={{ minWidth: 40 }}
                          >
                            <MenuItem value="" disabled>
                              <span role="img" aria-label="members">👥</span>
                            </MenuItem>
                            {project.employes?.map((employee, eIdx) => (
                              <MenuItem key={eIdx} value={employee.name} disabled>
                                {employee.name} : {getTaskName(employee.role)}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Loading row while fetching next page */}
                {loading && projects.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}

                {/* End marker */}
                {!hasMore && !loading && projects.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No more results
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Sentinel for IntersectionObserver */}
            <div ref={loadMoreRef} style={{ height: 1 }} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default ProjectListScreen;
